import React, { useEffect, useState } from "react";
import { Redirect, useParams } from "react-router-dom";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";

import AnchorButton from "../../components/AnchorButton";
import Button from "../../components/Button";
import Container from "../../components/Container";
import Divider from "../../components/Divider";
import Icon from "../../components/Icon";
import SelectInput from "../../components/SelectInput";
import Spacer from "../../components/Spacer";
import TextField from "../../components/TextField";
import addTicket from "../../helpers/addTicket";
import useTickets from "../../hooks/useTickets";
import { Controller, useForm } from "react-hook-form";
export interface TicketProps {
  code: string;
  code2?: string;
  email: string;
  firstname: string;
  lastname: string;
  order: string;
  status: string;
  type: string;
}

const AddTicket = () => {
  const { email } = useParams<{
    email?: string;
  }>();
  const tickets = useTickets();
  const [isRefreshed, setIsRefreshed] = useState(false);
  const [error, setError] = useState("");

  const {
    register,
    handleSubmit,
    errors,
    formState,
    control,
    setValue,
    trigger,
  } = useForm<TicketProps>({
    mode: "onChange",
  });

  // Initialize fields if ticket exists
  useEffect(() => {
    if (email) {
      const ticket = tickets.find((ticket) => ticket.email === email);

      if (ticket?.code) setValue("code", ticket.code);
      if (ticket?.code2) setValue("code2", ticket.code2);
      if (ticket?.email) setValue("email", ticket.email);
      if (ticket?.firstname) setValue("firstname", ticket.firstname);
      if (ticket?.lastname) setValue("lastname", ticket.lastname);
      if (ticket?.order) setValue("order", ticket.order);
      if (ticket?.status) setValue("status", ticket.status);
      if (ticket?.type) setValue("type", ticket.type);

      trigger();
    }
  }, [email, tickets, setValue, trigger]);

  const onSubmit = (data: TicketProps) => {
    setError("");
    addTicket(data);

    setTimeout(() => {
      setIsRefreshed(true);
    }, 1000);
  };

  return (
    <div className="AddTicketPage">
      <Container>
        <Spacer>
          <div className="my-4 text-lg font-medium text-aqua">Admin Form</div>
          <div className="my-4 text-5xl font-bold leading-none">
            {email ? "Edit Ticket" : "Add New Ticket"}
          </div>
          <Divider />
        </Spacer>

        <Spacer>
          <div className="w-full">
            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              <div className="mb-4 font-bold text-aqua">Email</div>
              <TextField
                type="email"
                name="email"
                placeholder="Email Address"
                register={register({
                  required: "Required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z0-9]{2,}$/i,
                    message: "Invalid email address.",
                  },
                })}
                error={errors.email?.message}
              />
              <div className="flex space-x-4">
                <div className="w-full">
                  <div className="mb-4 font-bold text-aqua">Ticket Code</div>
                  <Controller
                    control={control}
                    name="code"
                    defaultValue=""
                    rules={{
                      required: "Required",
                      minLength: {
                        value: 6,
                        message:
                          "Ticket code should have a minimum of 6 characters.",
                      },
                      setValueAs: (value) => value.toString().toUpperCase(),
                    }}
                    render={({ onChange, onBlur, value, name, ref }) => (
                      <TextField
                        type="text"
                        name={name}
                        placeholder="Ticket Code"
                        value={value.toString().toUpperCase()}
                        onChange={onChange}
                        onBlur={onBlur}
                        register={ref}
                        error={errors.code?.message}
                      />
                    )}
                  />
                </div>
                <div className="w-full">
                  <div className="mb-4 font-bold text-aqua">
                    Alternative Ticket Code
                  </div>
                  <Controller
                    control={control}
                    name="code2"
                    defaultValue=""
                    rules={{
                      setValueAs: (value) => value.toString().toUpperCase(),
                    }}
                    render={({ onChange, onBlur, value, name, ref }) => (
                      <TextField
                        type="text"
                        name={name}
                        placeholder="Alternative Ticket Code"
                        value={value.toString().toUpperCase()}
                        onChange={onChange}
                        onBlur={onBlur}
                        register={ref}
                        error={errors.code2?.message}
                      />
                    )}
                  />
                </div>
              </div>
              <div className="flex space-x-4">
                <div className="w-full">
                  <div className="mb-4 font-bold text-aqua">First Name</div>
                  <TextField
                    type="text"
                    name="firstname"
                    placeholder="First Name"
                    register={register({
                      required: "Required",
                    })}
                    error={errors.firstname?.message}
                  />
                </div>
                <div className="w-full">
                  <div className="mb-4 font-bold text-aqua">Last Name</div>
                  <TextField
                    type="text"
                    name="lastname"
                    placeholder="Last Name"
                    register={register({
                      required: "Required",
                    })}
                    error={errors.lastname?.message}
                  />
                </div>
              </div>
              <div className="flex flex-wrap space-y-4 lg:space-y-0 lg:flex-no-wrap lg:space-x-4">
                <div className="w-full">
                  <div className="mb-4 font-bold text-aqua">Ticket Type</div>
                  <Controller
                    control={control}
                    name="type"
                    defaultValue=""
                    rules={{
                      required: "Required",
                    }}
                    render={({ onChange, onBlur, value, name, ref }) => (
                      <SelectInput
                        name={name}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        error={errors.type?.message}
                        register={ref}
                        options={[
                          {
                            text: "SEAADE & IADR",
                            value: "SEAADE IADR",
                          },
                          {
                            text: "SEAADE",
                            value: "SEAADE",
                          },
                          {
                            text: "IADR",
                            value: "IADR",
                          },
                        ]}
                      />
                    )}
                  />
                </div>
                <div className="w-full">
                  <div className="mb-4 font-bold text-aqua">Ticket Order</div>
                  <TextField
                    type="text"
                    name="order"
                    placeholder="Order"
                    defaultValue="#00000-0000000"
                    register={register({
                      required: "Required",
                    })}
                    error={errors.order}
                  />
                </div>
                <div className="w-full">
                  <div className="mb-4 font-bold text-aqua">Status</div>
                  <Controller
                    control={control}
                    name="status"
                    defaultValue="active"
                    rules={{
                      required: "Required",
                    }}
                    render={({ onChange, onBlur, value, name, ref }) => (
                      <SelectInput
                        name={name}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        error={errors.type?.message}
                        register={ref}
                        options={[
                          {
                            text: "Active",
                            value: "active",
                          },
                          {
                            text: "Inactive",
                            value: "inactive",
                          },
                        ]}
                      />
                    )}
                  />
                </div>
              </div>
              <Spacer>
                <div className="flex items-center justify-start mt-8 space-x-4">
                  <Button
                    type="submit"
                    disabled={!formState.isValid}
                    isLoading={formState.isSubmitting}
                  >
                    Submit
                  </Button>
                  <AnchorButton href="/admin/tickets" theme="outline">
                    View Ticket List
                  </AnchorButton>
                </div>
              </Spacer>
            </form>
            {error ? (
              <div className="w-full mt-4 text-red-600 rounded">
                <Icon icon={faTimes} /> {error}
              </div>
            ) : null}
          </div>
          <div className="mt-4">
            {formState.isSubmitted ? (
              <div className="w-full my-2 text-sm text-green-600">
                <Icon icon={faCheck} /> Updated ticket successfully.
              </div>
            ) : null}

            {isRefreshed ? (
              <Redirect
                to={email ? `/admin/tickets/${email}` : "/admin/tickets/add"}
              />
            ) : null}
          </div>
        </Spacer>
      </Container>
    </div>
  );
};

export default AddTicket;
